/**
 * Entidade de Domínio: Lead (Deal)
 * Representa uma negociação/oportunidade com suas regras de negócio
 * Segue padrão similar ao Pipedrive: open, won, lost
 */

import { LeadValidator } from '../validation/LeadValidator';
import { LeadStatus, LeadOrigin } from '../types/LeadTypes';

export class Lead {
    constructor(
        public readonly id: string,
        public name: string,
        private email: string,
        private whatsApp: string,
        private value: number,
        public stage: string, // null quando won/lost
        public origin: LeadOrigin,
        public status: LeadStatus = 'open', // Status da negociação
        public agent: string | null = null,
        public tags: string[] = [],
        public observations: string = '',
        public readonly entryDate: Date = new Date(),
        public contactId?: string,
        public readonly createdBy?: string,
        public creatorName?: string,
        public stageEntryDate?: Date,
        // Campos de ganho/perda
        public wonAt?: Date,
        public lostAt?: Date,
        public lostReasonId?: string,
        public lossComments?: string // Comentários opcionais ao marcar como perdido
    ) {
        this.validate();
    }

    private validate(): void {
        LeadValidator.validateName(this.name);
        LeadValidator.validateEmail(this.email);
        LeadValidator.validateWhatsApp(this.whatsApp);
        LeadValidator.validateStage(this.status, this.stage);
        LeadValidator.validateValue(this.value);
    }

    // Getters
    getEmail(): string {
        return this.email;
    }

    getWhatsApp(): string {
        return this.whatsApp;
    }

    getValue(): number {
        return this.value;
    }

    // Métodos de negócio
    updateValue(newValue: number): void {
        LeadValidator.validateValue(newValue);
        this.value = newValue;
    }

    updateEmail(newEmail: string): void {
        LeadValidator.validateEmail(newEmail);
        this.email = newEmail;
    }

    updateWhatsApp(newPhone: string): void {
        LeadValidator.validateWhatsApp(newPhone);
        this.whatsApp = newPhone;
    }

    updateName(newName: string): void {
        LeadValidator.validateName(newName);
        this.name = newName;
    }

    assignAgent(agentName: string): void {
        this.agent = agentName;
    }

    addTag(tag: string): void {
        if (!this.tags.includes(tag)) {
            this.tags.push(tag);
        }
    }

    removeTag(tag: string): void {
        this.tags = this.tags.filter(t => t !== tag);
    }

    // Regras de negócio
    isHighValue(): boolean {
        return this.value >= 10000;
    }

    isAssigned(): boolean {
        return this.agent !== null && this.agent !== '';
    }

    canBeDeleted(): boolean {
        // Leads podem ser deletados apenas se estiverem abertos
        return this.status === 'open';
    }

    daysSinceEntry(): number {
        const now = new Date();
        const diff = now.getTime() - this.entryDate.getTime();
        return Math.floor(diff / (1000 * 60 * 60 * 24));
    }

    isStale(): boolean {
        // Lead é considerado "parado" se tiver mais de 30 dias sem movimentação
        return this.daysSinceEntry() > 30;
    }

    // ========== Métodos de Negócio para Win/Loss/Reopen ==========

    /**
     * Marca negociação como ganha
     * Remove do funil (stage = null)
     */
    markAsWon(): void {
        if (this.status === 'won') {
            throw new Error('Negociação já está marcada como ganha');
        }

        this.status = 'won';
        this.wonAt = new Date();
        // Mantém o stage original para identificar o funil de origem

        // Limpa campos de perda se existirem
        this.lostAt = undefined;
        this.lostReasonId = undefined;
        this.lossComments = undefined;
    }

    /**
     * Marca negociação como perdida
     * Requer motivo de perda
     * Remove do funil (stage = null)
     */
    markAsLost(lostReasonId: string, comments?: string): void {
        if (this.status === 'lost') {
            throw new Error('Negociação já está marcada como perdida');
        }

        if (!lostReasonId || lostReasonId.trim() === '') {
            throw new Error('Motivo de perda é obrigatório');
        }

        this.status = 'lost';
        this.lostAt = new Date();
        this.lostReasonId = lostReasonId;
        this.lossComments = comments;
        // Mantém o stage original para identificar o funil de origem

        // Limpa campos de ganho se existirem
        this.wonAt = undefined;
    }

    /**
     * Reabre uma negociação ganha ou perdida
     * Volta para o funil em uma etapa específica
     */
    reopen(newStageId: string): void {
        if (this.status === 'open') {
            throw new Error('Negociação já está aberta');
        }

        if (!newStageId || newStageId.trim() === '') {
            throw new Error('Etapa do funil é obrigatória ao reabrir negociação');
        }

        this.status = 'open';
        this.stage = newStageId;
        this.stageEntryDate = new Date();

        // Limpa campos de ganho/perda
        this.wonAt = undefined;
        this.lostAt = undefined;
        this.lostReasonId = undefined;
        this.lossComments = undefined;
    }

    /**
     * Verifica se a negociação está aberta (no funil)
     */
    isOpen(): boolean {
        return this.status === 'open';
    }

    /**
     * Verifica se a negociação foi ganha
     */
    isWon(): boolean {
        return this.status === 'won';
    }

    /**
     * Verifica se a negociação foi perdida
     */
    isLost(): boolean {
        return this.status === 'lost';
    }
}
